import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { graphBuilderApi } from '../../services/api';
import { getApiBaseUrl } from '../../utils/env';
import './SettingsPage.css';

/* ── tiny helper: colour-code a latency value ─────────────────── */
function rttBadge(ms) {
  if (ms == null) return null;
  let cls = 'latency-badge--good';
  if (ms > 1000) cls = 'latency-badge--warn';
  if (ms > 3000) cls = 'latency-badge--bad';
  return <span className={`latency-badge ${cls}`}>{ms.toFixed(0)} ms</span>;
}

function SettingsPage() {
  const [deployAvailable, setDeployAvailable] = useState(false);

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/deploy/status`, { credentials: 'include', headers: { 'Accept': 'application/json' } })
      .then((r) => r.json())
      .then((d) => setDeployAvailable(!!d.available))
      .catch(() => setDeployAvailable(false));
  }, []);

  const settingsOptions = [
    {
      key: 'llm-providers',
      label: 'LLM Providers',
      description: 'Configure LLM model deployments (OpenAI, DigitalOcean GPU) for graph building and execution.',
      to: '/user-home/llm-providers',
      icon: '🤖',
    },
    {
      key: 'coding-llm',
      label: 'Coding LLM',
      description: 'Configure high-power models (Claude Opus, Grok, GPT) for the Graph AI Agent and code-generation tasks.',
      to: '/user-home/coding-llm',
      icon: '⚡',
    },
    {
      key: 'prompt-templates',
      label: 'Prompt Templates',
      description: 'Browse and organize reusable prompt templates for your graphs.',
      to: '/user-home/prompt-templates',
      icon: '🧠',
    },
    ...(deployAvailable
      ? [
          {
            key: 'deployment',
            label: 'Deployment',
            description: 'Build and deploy services to Azure Container Apps from your local machine.',
            to: '/user-home/deployment',
            icon: '🚀',
          },
        ]
      : []),
    {
      key: 'transfer-environments',
      label: 'Transfer Environments',
      description: 'Configure target environments for copying graphs between deployments.',
      to: '/user-home/transfer-environments',
      icon: '🔄',
    },
    {
      key: 'monitoring',
      label: 'Monitoring',
      description: 'View container health, live logs, database clients, and uptime monitors.',
      to: '/user-home/monitoring',
      icon: '📡',
    },
    {
      key: 'cache-management',
      label: 'Cache Management',
      description: 'Inspect and clear Redis sessions, prompt caches, and compiled graph artifacts.',
      to: '/user-home/cache-management',
      icon: '🗄️',
    },
  ];

  /* ── Network latency probe state ────────────────────────────── */
  const [latency, setLatency] = useState(null);
  const [latencyLoading, setLatencyLoading] = useState(false);
  const [latencyError, setLatencyError] = useState(null);

  const runLatencyProbe = useCallback(async () => {
    setLatencyLoading(true);
    setLatencyError(null);
    try {
      const data = await graphBuilderApi.latencyProbe();
      setLatency(data);
    } catch (err) {
      setLatencyError(err?.message || 'Failed to reach GraphBuilder');
    } finally {
      setLatencyLoading(false);
    }
  }, []);

  return (
    <div className="user-home-layout__page settings-page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">Settings</h1>
        <p className="user-home-layout__page-subtitle">
          Configure your preferences and system settings
        </p>
      </div>

      <div className="user-home-layout__card">
        <h2 className="user-home-layout__section-title">Manage</h2>
        <p className="user-home-layout__card-text">
          Choose a configuration area to customize your workspace experience.
        </p>

        <div className="user-home-layout__settings-grid">
          {settingsOptions.map((option) => (
            <Link
              key={option.key}
              to={option.to}
              className="user-home-layout__settings-item"
            >
              <div className="user-home-layout__settings-icon" aria-hidden="true">
                {option.icon}
              </div>
              <div className="user-home-layout__settings-content">
                <h3 className="user-home-layout__settings-title">{option.label}</h3>
                <p className="user-home-layout__settings-description">
                  {option.description}
                </p>
              </div>
              <span className="user-home-layout__settings-chevron" aria-hidden="true">
                →
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Network Diagnostics card ──────────────────────────── */}
      <div className="user-home-layout__card" style={{ marginTop: 24 }}>
        <h2 className="user-home-layout__section-title">Network Diagnostics</h2>
        <p className="user-home-layout__card-text">
          Measure round-trip latency from the GraphBuilder backend to the database, Pinecone, and OpenAI.
        </p>

        <button
          className="latency-probe-btn"
          onClick={runLatencyProbe}
          disabled={latencyLoading}
        >
          {latencyLoading ? 'Measuring...' : 'Run Latency Probe'}
        </button>

        {latencyError && (
          <p className="latency-error">{latencyError}</p>
        )}

        {latency && !latencyError && (
          <div className="latency-results">
            <div className="latency-row">
              <span className="latency-label">Database (PostgreSQL)</span>
              <span className="latency-status">{latency.database?.status}</span>
              {rttBadge(latency.database?.rtt_ms)}
            </div>
            <div className="latency-row">
              <span className="latency-label">Pinecone</span>
              <span className="latency-status">{latency.pinecone?.status}</span>
              {rttBadge(latency.pinecone?.rtt_ms)}
            </div>
            <div className="latency-row">
              <span className="latency-label">OpenAI ({latency.openai?.model || 'embedding'})</span>
              <span className="latency-status">{latency.openai?.status}</span>
              {rttBadge(latency.openai?.rtt_ms)}
            </div>
            <div className="latency-row latency-row--total">
              <span className="latency-label">Total RTT</span>
              {rttBadge(latency.total_rtt_ms)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SettingsPage;



